import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Camera, Car, KeyRound, Mail, Minus, Phone, Plus, UserRound } from 'lucide-react';
import { BottomSheet } from '@shared/ui/containers/BottomSheet';
import { Card } from '@shared/ui/containers/Card';
import { Button } from '@shared/ui/primitives/Button';
import {
  InviteRuleInfo,
  RadioOption,
  SelectField,
  TextInputField,
} from '@shared/ui/forms/FormFields';
import { Banner } from '@shared/ui/feedback/Banner';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { toast } from '@shared/ui/feedback/Toast';
import { publicBaseUrl } from '@shared/lib/platform';
import { AccessInvitePropertyPicker } from './AccessInvitePropertyPicker';
import { formatAccessInviteLocationLabel } from './accessInviteLocationUi';
import { toApiError } from '@shared/api/errors';
import {
  createAccessInvite,
  fetchAccessEntryTypesForInvite,
  fetchAccessInviteLocations,
  type AccessInvite,
  type InviteCaptureRequirements,
  type MemberAccessEntryTypeOption,
  type MemberInviteCredentials,
} from './api';
import { AccessEntryTypeIcon } from './AccessEntryTypeIcon';
import { fetchMemberContacts, type MemberContact } from './memberContactsApi';

type WizardStep = 'type' | 'details';
type VisitorMode = 'saved' | 'adhoc';
type VisitDay = 'today' | 'tomorrow';

const FALLBACK_DEFAULT_VALIDITY_DAYS = 7;

/** Prefill from an existing invite (re-invite). Dates/duration are always reset. */
interface NewAccessInviteSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialContactId?: string;
  /** Preselect an access type (e.g. opened from a dashboard entry-type tile) — skips the type step. */
  initialEntryTypeId?: string;
  /** Existing invite to copy visitor, type, and property from (validity reset to default). */
  reinviteFrom?: AccessInvite | null;
  onCreated?: (invite: AccessInvite, credentials?: MemberInviteCredentials) => void;
}

export function NewAccessInvitePage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  return (
    <div className="min-h-full bg-paper">
      <NewAccessInviteSheet
        open
        initialContactId={searchParams.get('contact_id') ?? undefined}
        onCreated={(invite) => {
          navigate(`/app/access-invites?invite=${invite.access_invite_id}`, { replace: true });
        }}
        onOpenChange={(open) => {
          if (!open) navigate('/app/access-invites');
        }}
      />
    </div>
  );
}

export function NewAccessInviteSheet({
  open,
  onOpenChange,
  initialContactId,
  initialEntryTypeId,
  reinviteFrom,
  onCreated,
}: NewAccessInviteSheetProps): JSX.Element {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<WizardStep>('type');
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [visitorMode, setVisitorMode] = useState<VisitorMode>('saved');
  const [selectedContactId, setSelectedContactId] = useState('');
  const [visitorName, setVisitorName] = useState('');
  const [visitorPhone, setVisitorPhone] = useState('');
  const [visitorEmail, setVisitorEmail] = useState('');
  const [vehicleReg, setVehicleReg] = useState('');
  const [totalAdults, setTotalAdults] = useState(1);
  const [totalMinors, setTotalMinors] = useState(0);
  const [locationId, setLocationId] = useState('');
  const [visitDay, setVisitDay] = useState<VisitDay>('today');
  const [error, setError] = useState('');

  const typesQuery = useQuery({
    queryKey: ['access-entry-types'],
    queryFn: fetchAccessEntryTypesForInvite,
    enabled: open,
  });
  const locationsQuery = useQuery({
    queryKey: ['access-invite-locations'],
    queryFn: fetchAccessInviteLocations,
    enabled: open,
  });
  const contactsQuery = useQuery({
    queryKey: ['member-contacts'],
    queryFn: fetchMemberContacts,
    enabled: open,
  });

  const activeContacts = useMemo(
    () => (contactsQuery.data ?? []).filter((contact) => contact.is_active !== false),
    [contactsQuery.data],
  );
  const selectedType = typesQuery.data?.find((type) => type.access_entry_type_id === selectedTypeId);
  // Resolved visitor-field capture rules (channel ceiling ∩ entry-type opt-in). When the backend
  // predates capture_requirements, fall back to the legacy shape: email optional, phone required.
  const captureReqs: InviteCaptureRequirements = selectedType?.capture_requirements ?? {
    email: { capture: true, required: false },
    phone: { capture: true, required: true },
    vehicle_reg: { capture: false, required: false },
    headcount: { capture: false, required: false },
  };
  const selfieRequired = Boolean(selectedType?.require_visitor_selfie_default);
  const allowMultipleEntries = Boolean(selectedType?.allow_multiple_entries);
  const defaultValidityDays = selectedType?.default_validity_days ?? FALLBACK_DEFAULT_VALIDITY_DAYS;
  const validityLabel =
    defaultValidityDays === 1 ? '1 day' : `${defaultValidityDays} days`;

  const mirrorContact = (contact?: MemberContact) => {
    setVisitorName(contact?.display_name ?? '');
    setVisitorPhone(contact?.phone ?? '');
    setVisitorEmail(contact?.email ?? '');
  };

  const sheetTitle =
    step === 'type'
      ? 'Choose access type'
      : reinviteFrom
        ? 'Invite again'
        : 'Invite a visitor';
  const sheetDescription =
    step === 'type'
      ? 'Select the access type for this invite.'
      : reinviteFrom
        ? 'Review the visitor details before sending.'
        : 'Add the visitor and visit details.';

  useEffect(() => {
    if (!open) return;
    const entryTypeId =
      reinviteFrom?.access_entry_type_id ?? initialEntryTypeId ?? '';
    setStep(entryTypeId ? 'details' : 'type');
    setSelectedTypeId(entryTypeId);
    setSelectedContactId('');
    setLocationId(reinviteFrom?.location_id ?? '');
    setVisitDay('today');
    setVisitorMode('saved');
    setVisitorName('');
    setVisitorPhone('');
    setVisitorEmail('');
    setVehicleReg('');
    setTotalAdults(1);
    setTotalMinors(0);
    setError('');
  }, [open, initialEntryTypeId, reinviteFrom]);

  useEffect(() => {
    if (!open || contactsQuery.isLoading) return;

    if (reinviteFrom?.member_contact_id) {
      const contact = activeContacts.find(
        (row) => row.member_contact_id === reinviteFrom.member_contact_id,
      );
      if (contact) {
        setVisitorMode('saved');
        setSelectedContactId(contact.member_contact_id);
        mirrorContact(contact);
        return;
      }
    }

    if (reinviteFrom) {
      setVisitorMode('adhoc');
      setSelectedContactId('');
      setVisitorName(reinviteFrom.visitor_name ?? '');
      setVisitorPhone(reinviteFrom.visitor_phone ?? '');
      setVisitorEmail(reinviteFrom.visitor_email ?? '');
      return;
    }

    const preset =
      initialContactId &&
      activeContacts.find((contact) => contact.member_contact_id === initialContactId);
    if (preset) {
      setVisitorMode('saved');
      setSelectedContactId(preset.member_contact_id);
      mirrorContact(preset);
      return;
    }
    if (activeContacts.length === 0) setVisitorMode('adhoc');
  }, [activeContacts, contactsQuery.isLoading, initialContactId, open, reinviteFrom]);

  useEffect(() => {
    const rows = locationsQuery.data ?? [];
    const sole = rows.length === 1 ? rows[0] : undefined;
    if (!sole) return;
    setLocationId((current) => current || sole.location_id);
  }, [locationsQuery.data]);

  const mutation = useMutation({
    mutationFn: createAccessInvite,
    onSuccess: ({ invite, credentials }) => {
      queryClient.setQueryData<AccessInvite[]>(['access-invites'], (current) => upsertInvite(current, invite));
      void queryClient.invalidateQueries({ queryKey: ['access-invites'] });
      toast.success('Invite created');
      onCreated?.(invite, credentials);
      if (!onCreated) onOpenChange(false);
    },
    onError: (e) => {
      const message = toApiError(e).message || 'Create failed';
      const timedOut = /timeout/i.test(message);
      if (timedOut) {
        void queryClient.invalidateQueries({ queryKey: ['access-invites'] });
        setError(
          'The request timed out, but your invite may still have been created. Check the invites list.',
        );
        toast.danger(
          'Request timed out',
          'If the new invite appears in your list, it was created successfully.',
        );
        return;
      }
      setError(message);
      toast.danger("Couldn't create invite", message);
    },
  });

  const selectType = (type: MemberAccessEntryTypeOption) => {
    setSelectedTypeId(type.access_entry_type_id);
    setStep('details');
    setError('');
  };

  const submit = () => {
    if (!selectedTypeId) {
      setError('Select an access type.');
      return;
    }
    if (visitorMode === 'saved' && !selectedContactId) {
      setError('Select a saved visitor or switch to add a new visitor.');
      return;
    }
    const name = visitorName.trim();
    const phone = captureReqs.phone.capture ? normalizePhone(visitorPhone) : '';
    const email = captureReqs.email.capture ? visitorEmail.trim() : '';
    const plate = captureReqs.vehicle_reg.capture ? vehicleReg.trim() : '';
    if (!name) {
      setError('Enter the visitor name.');
      return;
    }
    if (captureReqs.phone.required && !phone) {
      setError('Enter the visitor mobile number.');
      return;
    }
    if (captureReqs.email.required && !email) {
      setError('Enter the visitor email address.');
      return;
    }
    if (captureReqs.vehicle_reg.required && !plate) {
      setError('Enter the visitor vehicle registration.');
      return;
    }
    const locations = locationsQuery.data ?? [];
    if (locations.length === 0) {
      setError('No property is linked to your account. Link or verify a property before creating visitor invites.');
      return;
    }
    if (locations.length > 1 && !locationId) {
      setError('Select your property.');
      return;
    }
    const soleLocation = locations.length === 1 ? locations[0] : undefined;
    const resolvedLocationId = locationId || soleLocation?.location_id || '';
    const selectedLocation = locations.find((row) => row.location_id === resolvedLocationId);
    if (selectedLocation && !selectedLocation.location_code?.trim()) {
      setError(
        `${formatAccessInviteLocationLabel(selectedLocation)} has no gate code configured. Ask estate management to set a property code.`,
      );
      return;
    }
    const validFrom = startOfVisitDay(visitDay);
    setError('');
    mutation.mutate({
      access_entry_type_id: selectedTypeId,
      location_id: resolvedLocationId || null,
      member_contact_id: visitorMode === 'saved' ? selectedContactId : null,
      visitor_name: name,
      visitor_phone: phone || null,
      visitor_email: email || null,
      vehicle_reg: plate || null,
      total_adults: captureReqs.headcount.capture ? totalAdults : null,
      total_minors: captureReqs.headcount.capture ? totalMinors : null,
      valid_from: validFrom.toISOString(),
      public_base_url: publicBaseUrl(),
    });
  };

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title={sheetTitle}
      description={sheetDescription}
      height="92dvh"
    >
      {step === 'type' ? (
        <div className="flex flex-col gap-3 pb-4">
          {typesQuery.isLoading ? (
            <>
              <Skeleton className="h-20" />
              <Skeleton className="h-20" />
            </>
          ) : typesQuery.isError ? (
            <Banner variant="danger" title="Could not load access types" />
          ) : (typesQuery.data ?? []).length === 0 ? (
            <Banner title="No access types available" description="Ask estate management to enable visitor invite types." />
          ) : (
            typesQuery.data!.map((type) => (
              <Card
                key={type.access_entry_type_id}
                asChild
                noGrain
                className="rounded-xl p-4 text-left shadow-none transition-colors hover:bg-sunken"
              >
                <button
                  type="button"
                  onClick={() => selectType(type)}
                  className="flex w-full items-center gap-3.5 text-left"
                >
                  <AccessEntryTypeIcon entryType={type} tone="default" />
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-headline text-ink">{type.name}</p>
                    {type.code ? (
                      <p className="mt-1 text-caption text-ink-muted">{type.code}</p>
                    ) : null}
                  </div>
                </button>
              </Card>
            ))
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4 pb-4">
          {error ? <Banner variant="danger" title="Check invite details" description={error} /> : null}

          <Card noGrain className="p-4">
            <p className="font-display text-headline text-ink">{selectedType?.name ?? 'Access invite'}</p>
            {selectedType?.code ? (
              <p className="mt-1 text-caption text-ink-muted">{selectedType.code}</p>
            ) : null}
          </Card>

          {activeContacts.length > 0 ? (
            <div className="grid grid-cols-2 gap-2">
              <RadioOption
                label="Saved visitor"
                value="saved"
                selected={visitorMode === 'saved'}
                onChange={() => setVisitorMode('saved')}
              />
              <RadioOption
                label="Add new"
                value="adhoc"
                selected={visitorMode === 'adhoc'}
                onChange={() => {
                  setVisitorMode('adhoc');
                  setSelectedContactId('');
                  mirrorContact(undefined);
                }}
              />
            </div>
          ) : null}

          {visitorMode === 'saved' ? (
            <SelectField
              label="Visitor"
              value={selectedContactId}
              onChange={(event) => {
                const contact = activeContacts.find((row) => row.member_contact_id === event.target.value);
                setSelectedContactId(event.target.value);
                mirrorContact(contact);
              }}
            >
              <option value="">Choose a saved visitor</option>
              {activeContacts.map((contact) => (
                <option key={contact.member_contact_id} value={contact.member_contact_id}>
                  {contact.display_name}
                </option>
              ))}
            </SelectField>
          ) : null}

          <TextInputField
            label="Visitor name"
            value={visitorName}
            onChange={(event) => setVisitorName(event.target.value)}
            startAdornment={<UserRound className="h-4 w-4" strokeWidth={1.5} />}
          />
          {captureReqs.phone.capture ? (
            <TextInputField
              label={captureReqs.phone.required ? 'Visitor mobile' : 'Visitor mobile (optional)'}
              type="tel"
              inputMode="tel"
              value={visitorPhone}
              onChange={(event) => setVisitorPhone(event.target.value)}
              helper="Used for gate access and invite notifications."
              startAdornment={<Phone className="h-4 w-4" strokeWidth={1.5} />}
            />
          ) : null}
          {captureReqs.email.capture ? (
            <TextInputField
              label={captureReqs.email.required ? 'Visitor email' : 'Visitor email (optional)'}
              type="email"
              value={visitorEmail}
              onChange={(event) => setVisitorEmail(event.target.value)}
              helper={captureReqs.email.required ? undefined : 'Optional.'}
              startAdornment={<Mail className="h-4 w-4" strokeWidth={1.5} />}
            />
          ) : null}
          {captureReqs.vehicle_reg.capture ? (
            <TextInputField
              label={captureReqs.vehicle_reg.required ? 'Vehicle registration' : 'Vehicle registration (optional)'}
              value={vehicleReg}
              onChange={(event) => setVehicleReg(event.target.value)}
              helper="Used for number-plate recognition at the gate."
              startAdornment={<Car className="h-4 w-4" strokeWidth={1.5} />}
            />
          ) : null}
          {captureReqs.headcount.capture ? (
            <div className="grid grid-cols-2 gap-3">
              <CountStepper label="Adults" value={totalAdults} min={1} onChange={setTotalAdults} />
              <CountStepper label="Minors" value={totalMinors} min={0} onChange={setTotalMinors} />
            </div>
          ) : null}

          <AccessInvitePropertyPicker
            locations={locationsQuery.data ?? []}
            selectedId={locationId}
            onSelect={setLocationId}
            isLoading={locationsQuery.isLoading}
            isError={locationsQuery.isError}
          />

          <div>
            <p className="mb-2 text-body-sm font-medium text-ink">Visit day</p>
            <div className="flex gap-2">
              {(['today', 'tomorrow'] as VisitDay[]).map((day) => (
                <button
                  key={day}
                  type="button"
                  onClick={() => setVisitDay(day)}
                  className={
                    'rounded-pill px-4 py-2 text-body-sm capitalize transition-colors ' +
                    (visitDay === day
                      ? 'bg-primary-600 text-white'
                      : 'bg-sunken text-ink-secondary hover:bg-card')
                  }
                >
                  {day}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-caption font-medium uppercase tracking-wide text-ink-muted">Visit rules</p>
            <InviteRuleInfo
              icon={<CalendarDays className="h-4 w-4" strokeWidth={1.5} />}
              label={`Valid for ${validityLabel}`}
              description="Set by your estate for this access type."
              active
              statusLabel="Applies"
            />
            <InviteRuleInfo
              icon={<KeyRound className="h-4 w-4" strokeWidth={1.5} />}
              label={allowMultipleEntries ? 'Multiple entries' : 'Single entry'}
              description={
                allowMultipleEntries
                  ? 'The visitor may enter and exit the estate multiple times during the validity period.'
                  : 'The visitor can use the gate once during the validity period.'
              }
              active
              statusLabel="Applies"
            />
            <InviteRuleInfo
              icon={<Camera className="h-4 w-4" strokeWidth={1.5} />}
              label="Require visitor selfie"
              description={
                selfieRequired
                  ? 'The visitor must capture a photo before the invite can be used.'
                  : 'No visitor selfie is required for this access type.'
              }
              active={selfieRequired}
              statusLabel={selfieRequired ? 'Required' : 'Not required'}
            />
          </div>

          <div className="mt-2 grid grid-cols-2 gap-3">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button loading={mutation.isPending} onClick={submit}>Create invite</Button>
          </div>
        </div>
      )}
    </BottomSheet>
  );
}

interface CountStepperProps {
  label: string;
  value: number;
  min: number;
  onChange: (value: number) => void;
}

/** Compact numeric stepper for party headcount (adults / minors). */
function CountStepper({ label, value, min, onChange }: CountStepperProps): JSX.Element {
  const clamp = (n: number) => Math.max(min, Math.min(n, 99));
  return (
    <div>
      <p className="mb-2 text-body-sm font-medium text-ink">{label}</p>
      <div className="flex items-center justify-between rounded-xl bg-sunken px-3 py-2">
        <button
          type="button"
          aria-label={`Decrease ${label}`}
          onClick={() => onChange(clamp(value - 1))}
          disabled={value <= min}
          className="flex h-8 w-8 items-center justify-center rounded-pill bg-card text-ink-secondary transition-colors hover:bg-paper disabled:opacity-40"
        >
          <Minus className="h-4 w-4" strokeWidth={1.5} />
        </button>
        <span className="font-display text-headline text-ink">{value}</span>
        <button
          type="button"
          aria-label={`Increase ${label}`}
          onClick={() => onChange(clamp(value + 1))}
          className="flex h-8 w-8 items-center justify-center rounded-pill bg-card text-ink-secondary transition-colors hover:bg-paper"
        >
          <Plus className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}

function upsertInvite(current: AccessInvite[] | undefined, invite: AccessInvite): AccessInvite[] {
  if (!current) return [invite];
  return [...current.filter((row) => row.access_invite_id !== invite.access_invite_id), invite];
}

function normalizePhone(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  if (value.trim().startsWith('+')) return `+${digits}`;
  if (digits.startsWith('27')) return `+${digits}`;
  return `+27${digits.replace(/^0+/, '')}`;
}

function startOfVisitDay(day: VisitDay): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (day === 'tomorrow') d.setDate(d.getDate() + 1);
  return d;
}
